import { Box, Button, useTheme } from "@mui/material";
import { t } from "i18next";
import { TQ3DSelectItem, Q3DSelect } from "../../Parts/Q3DSelect";
import { useContext, useEffect, useRef, useState } from "react";
import {
  ECoordinateTransformMode,
  ECoordInputMode,
} from "../../config/componentsConfig";
import Q3DIconButton from "../../Parts/Q3DIconButton";
import { Common } from "../../../../common/src";
import axios, { AxiosResponse } from "axios";
import iconv from "iconv-lite";
import { config } from "../../../config/config";
import {
  EDisplayType,
  ProjectDetailContext,
} from "../../../pages/ProjectDetail";
import HelmContext from "../Contexts/HelmContext";
import dayjs from "dayjs";
import { DocumentImportIcon } from "../../Parts/Q3DIcons";

type Props = {};

enum ECoordinate {
  MATH = "MATH",
  GEO = "GEO",
}

const FileTab = () => {
  const [coordinate, setCoordinate] = useState<ECoordinate>(ECoordinate.MATH);
  const { projectDetail, displayType } = useContext(ProjectDetailContext);
  const { fetchCoordinatePoints } = useContext(HelmContext);
  const inputRef = useRef(null);
  const theme = useTheme();

  const handleButtonClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const fileItems = [
    {
      value: ECoordinate.MATH,
      label: t("X_NORTH_SOUTH"),
    },
    {
      value: ECoordinate.GEO,
      label: t("X_EAST_WEST"),
    },
  ] as TQ3DSelectItem[];

  const importFromFile = async (e: any) => {
    try {
      e.preventDefault();
      // setIsCoordinateFetching(true);
      const reader = new FileReader();
      const file_type: string = e.target.files[0].name.split(".").pop();
      // 登録用データ
      let registeringPoints: Common.PointCoordinateForRegister[] = [];
      switch (file_type.toUpperCase()) {
        case "CSV":
        case "TXT":
          reader.onload = async (e: any) => {
            const UniText = iconv.decode(e.target.result, "UTF-8");
            const dataString = UniText.match(/�/)
              ? String(iconv.decode(e.target.result, "Shift_JIS")).split("\r\n")
              : String(UniText).split("\r\n");
            const dataArray = []; //配列を用意
            for (let i = 0; i < dataString.length; i++) {
              //あるだけループ
              dataArray[i] = dataString[i].split(",");
            }
            dataArray.forEach((r) => {
              if (r.length === 4) {
                const registeredPoint: Common.PointCoordinateForRegister =
                  coordinate === fileItems[0].value
                    ? {
                        name: String(r[0]) || "",
                        x: Number(r[1]) || 0,
                        y: Number(r[2]) || 0,
                        z: Number(r[3]) || 0,
                        timeStamp: null,
                      }
                    : {
                        name: String(r[0]) || "",
                        x: Number(r[2]) || 0,
                        y: Number(r[1]) || 0,
                        z: Number(r[3]) || 0,
                        timeStamp: null,
                      };
                registeringPoints.push(registeredPoint);
              }
            });
          };
          break;
        case "SIM":
          reader.onload = async (e: any) => {
            const UniText = iconv.decode(e.target.result, "UTF-8");
            const dataString = UniText.match(/�/)
              ? String(iconv.decode(e.target.result, "Shift_JIS")).split("\r\n")
              : String(UniText).split("\r\n");
            const dataArray = []; //配列を用意
            let start = false; //開始フラグ
            let end = false; //終了フラグ
            //simaの読み方
            //https://asakaze-net.jp/sima/excel-sima.asp
            for (let i = 0; i < dataString.length; i++) {
              dataString[i].split(",");
              //終了コード判定
              if (!end) {
                if (dataString[i].split(",")[0] === "A99") {
                  end = true;
                }
              }
              //あるだけループ
              if (start && !end) {
                dataArray[i] = dataString[i].split(",");
              }
              //開始コード判定
              if (!start) {
                if (dataString[i].split(",")[0] === "A00") {
                  start = true;
                }
              }
            }
            dataArray.forEach((r) => {
              const registeredPoint: Common.PointCoordinateForRegister = {
                name: String(r[2]) || "",
                x: Number(r[3]) || 0,
                y: Number(r[4]) || 0,
                z: Number(r[5]) || 0,
                timeStamp: null,
              };
              registeringPoints.push(registeredPoint);
            });
          };
          break;
        default:
          break;
      }
      // 読み込み完了コールバック
      reader.onloadend = async () => {
        if (registeringPoints.length === 0) {
          // setIsCoordinateFetching(false);
          return;
        }
        // data 登録
        const body: Common.PointsCoordinateDataForRegister = {
          points: registeringPoints,
        };
        const isRegistered = await axios
          .post(
            `${config.serverHost}/api/v1/project/${projectDetail?.id}/points/coordinate`,
            body,
            { withCredentials: true }
          )
          .then((res: AxiosResponse) => {
            return true;
          })
          .catch((err) => {
            return false;
          });
        // .finally(() => { setIsCoordinateFetching(false); });
        if (!isRegistered) {
          return;
        }
        // 座標情報再読み込み
        fetchCoordinatePoints(projectDetail?.id);
      };
      // 読み込み
      reader.readAsBinaryString(e.target.files[0]);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <Q3DSelect
        label={t("COORDINATE_SYSTEM")}
        items={fileItems}
        value={coordinate}
        setValue={(v: string) => {
          setCoordinate(v as ECoordinate);
        }}
        height={"36px"}
        sx={{ fontSize: "14px" }}
      />
      <Q3DIconButton
        title={t("IMPORT")}
        titleType="tooltip"
        placement="top-start"
        icon={<DocumentImportIcon width={32} height={32} />}
        width="36px"
        height="36px"
        onClick={handleButtonClick}
        sx={{
          background: theme.palette.base.ymdBackground,
          "& .injected-svg": {
            fill: "#FFFFFF !important",
          },
          "&:hover": {
            background: "#736CED",
          },
        }}
      />
      <input
        ref={inputRef}
        type="file"
        accept={
          displayType === EDisplayType.iOS ? "application/octet-stream" : ""
        }
        id="display-none"
        value=""
        onChange={importFromFile}
        onClick={(e: any) => {
          e.target.values = "";
        }}
      />
    </>
  );
};

type TRoverData = {
  label: string;
  name: string;
  date: string;
};

const RoverTab = () => {
  const { projectDetail } = useContext(ProjectDetailContext);
  const { currentHelmTab, fetchCoordinatePoints } = useContext(HelmContext);

  const [selectedRoverPointGroup, setSelectedRoverPointGroup] =
    useState<string>("");
  const [roverPointData, setRoverPointData] = useState<Common.SCRoverPointData>(
    { pointGroups: [] }
  );
  const [roverTimeStampList, setRoverTimeStampList] = useState<string[]>([]);
  const [pointDateTimeStamp, setPointDateTimeStamp] = useState<string>("");
  const [getPointDateTimeStamp, setGetPointDateTimeStamp] =
    useState<string>("");

  const [roverData, setRoverData] = useState<TQ3DSelectItem[]>([]);
  const [roverDataMap, setRoverDataMap] =
    useState<Map<string, TRoverData>>(null);
  const [selectRoverData, setSelectRoverData] = useState<string>("0");

  const theme = useTheme();

  useEffect(() => {
    if (currentHelmTab === ECoordinateTransformMode.INPUT) {
      getFromRover();
    }
  }, [currentHelmTab]);

  const getFromRover = async () => {
    // setIsCoordinateFetching(true);
    await axios
      .get(`${config.serverHost}/api/v1/project/${projectDetail?.id}/rover`, {
        withCredentials: true,
      })
      .then((res: AxiosResponse<Common.SCRoverPointData>) => {
        setRoverPointData(res.data);
        const tmpRoverDataMap: Map<string, TRoverData> = new Map();
        const roverDataItems: TQ3DSelectItem[] = [];
        let key = 0;
        let init = false;
        res.data.pointGroups.forEach((point) => {
          const timeList = dateFormatSort(point.points);
          timeList.forEach((time) => {
            tmpRoverDataMap.set(String(key), {
              name: point.name,
              date: time,
            } as TRoverData);
            roverDataItems.push({
              label: `${point.name} ${time}`,
              value: String(key),
            });
            key++;
          });
        });
        setRoverDataMap(tmpRoverDataMap);
        setRoverData(roverDataItems);
        setSelectRoverData("0");
        if (!init) {
          setSelectedRoverPointGroup(tmpRoverDataMap.get("0")?.name);
          setPointDateTimeStamp(tmpRoverDataMap.get("0")?.date);
        }
      })
      .finally(() => {
        // setIsCoordinateFetching(false);
      });
  };

  const dateFormatSort = (dT: any): string[] => {
    let uniqueArr: string[] = [];

    for (const idx in dT) {
      if (!uniqueArr.includes(dayjs(dT[idx].timeStamp).format(`YYYY/MM/DD`))) {
        uniqueArr.push(dayjs(dT[idx].timeStamp).format(`YYYY/MM/DD`));
      }
    }
    uniqueArr.sort((n1: string, n2: string) => {
      if (n1 < n2) return 1;
      if (n1 > n2) return -1;
      return 0;
    });
    setPointDateTimeStamp(uniqueArr[0]);
    return uniqueArr;
  };

  const importFromRover = async () => {
    try {
      if (!selectedRoverPointGroup) {
        return;
      }
      const selectedGroup = roverPointData.pointGroups.filter(
        (g) => g.name === selectedRoverPointGroup
      )[0];
      if (!selectedGroup) {
        return;
      }

      const dateRegex = /[\\/]/gi;
      const roverDate =
        getPointDateTimeStamp === ""
          ? pointDateTimeStamp.replace(dateRegex, "-")
          : getPointDateTimeStamp.replace(dateRegex, "-");

      // 選択された日付のみ反映される。
      const registeringPoints = selectedGroup.points.filter((p) => {
        let pCalenderVal: string = p.timeStamp.toString().split("T")[0];
        if (roverDate === pCalenderVal) {
          return {
            name: p.name,
            x: p.x,
            y: p.y,
            z: p.z,
            timeStamp: p.timeStamp,
          } as Common.PointCoordinateForRegister;
        }
      });

      const body: Common.PointsCoordinateDataForRegister = {
        points: registeringPoints,
      };

      // setIsCoordinateFetching(true);
      await axios
        .post(
          `${config.serverHost}/api/v1/project/${projectDetail?.id}/points/coordinate`,
          body,
          { withCredentials: true }
        )
        .then((res: AxiosResponse) => {
          return true;
        })
        .catch(() => {
          return false;
        });

      // 座標情報再読み込み
      fetchCoordinatePoints(projectDetail?.id);
      setPointDateTimeStamp(pointDateTimeStamp);
    } catch (e) {
      console.error(e);
    }
  };

  const changePointDate = (dateVal: string) => {
    setGetPointDateTimeStamp(dateVal);
    setPointDateTimeStamp(dateVal);
  };

  return (
    <>
      <Q3DSelect
        label={roverData?.length > 0 && t("NAME")}
        items={roverData}
        value={selectRoverData}
        setValue={(v) => {
          setSelectRoverData(v);
          const selected = roverDataMap.get(v);
          setSelectedRoverPointGroup(selected.name);
          setPointDateTimeStamp(selected.date);
        }}
        height={"36px"}
        sx={{ fontSize: "14px" }}
      />
      <Q3DIconButton
        title={t("IMPORT")}
        titleType="tooltip"
        placement="top-start"
        icon={<DocumentImportIcon width={32} height={32} />}
        width="36px"
        height="36px"
        onClick={importFromRover}
        sx={{
          background: theme.palette.base.ymdBackground,
          "& .injected-svg": {
            fill:
              !selectedRoverPointGroup && !pointDateTimeStamp
                ? theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.3) !important"
                  : "rgba(0, 0, 0, 0.3) !important"
                : "#FFFFFF !important",
          },
          "&:hover": {
            background: "#736CED",
          },
        }}
        disabled={!selectedRoverPointGroup && !pointDateTimeStamp}
      />
    </>
  );
};

export const HelmInput = (props: Props) => {
  const { displayType } = useContext(ProjectDetailContext);

  const [inputMode, setInputMode] = useState<ECoordInputMode>(
    ECoordInputMode.ROVER
  );

  const helmInputItems = [
    {
      value: ECoordInputMode.ROVER,
      label: "Smart Construction Rover",
    },
    {
      value: ECoordInputMode.FILE,
      label: t("FILE"),
    },
  ] as TQ3DSelectItem[];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-end",
        gap: "var(--space-space--4, 4px)",
      }}
    >
      <Q3DSelect
        label="type"
        items={helmInputItems}
        value={inputMode}
        setValue={(v) => {
          setInputMode(v as ECoordInputMode);
        }}
        height={"36px"}
        maxWidth={displayType === EDisplayType.Web ? "infinity" : "120px"}
        sx={{ fontSize: "14px" }}
      />
      {inputMode === ECoordInputMode.FILE ? <FileTab /> : <RoverTab />}
    </Box>
  );
};
